/* eslint-disable tailwindcss/no-arbitrary-value */
import React, {
  useEffect, useRef, useContext, useState, useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  RadioGroup, Radio, Button,
  SelectionControlLabel,
  DrawerFooter, DrawerBody,
  SkeletonLine,
  Alert, Typography, Link
} from '@one-thd/sui-atomic-components';
import { InlineProjectCreation } from '@thd-olt-component-react/project-forms';
import { AddToProductDetails } from '../../common/AddToProductDetails/AddToProductDetails';
import { ProjectSearch } from './ProjectSearch';
import { SCREENS, TRACK, SELECTION_TYPE, LAST_USED_PROJECT_KEY } from '../../AddToConstants';
import { triggerAnalytics } from '../../AddToHelpers';
import { AddToCoreContext } from '../../core/AddToCoreContext';

export const SelectProject = ({
  products,
  singleProduct,
  multipleProduct,
  projects,
  projectsExist,
  setSelectedProject,
  pageSize,
  setPage,
  page,
  projectsCount,
  loading,
  setInitialLoad,
  onInlineCreationSuccess,
  showInlineSuccessAlert
}) => {
  const scrollableDivRef = useRef(null);
  const sentinelRef = useRef(null);
  const { setScreen, handleClose } = useContext(AddToCoreContext);
  const [projectInformation, setProjectInformation] = useState({
    projectId: '', projectName: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdProjectName, setCreatedProjectName] = useState('');
  const [createdProjectId, setCreatedProjectId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const autoSelectRef = useRef(null);
  const hasAutoFocusedRef = useRef(false);
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return projects;
    return projects.filter((project) => project.projectName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [projects, searchTerm]);
  const onSelectProject = (project) => {
    setProjectInformation({
      projectId: project?.id,
      projectName: project?.projectName
    });
  };
  const addItemToProject = () => {
    setSelectedProject({
      projectId: projectInformation.projectId,
      projectName: projectInformation.projectName
    });
  };

  useEffect(() => {
    triggerAnalytics(TRACK.COPY_TO_SELECT_OVERLAY, { selectionType: SELECTION_TYPE.PROJECT });
    try {
      const stored = JSON.parse(localStorage.getItem(LAST_USED_PROJECT_KEY));
      if (stored?.projectId) {
        setProjectInformation({ projectId: stored.projectId, projectName: stored.projectName });
      }
    } catch (e) { /* no-op */ }
  }, []);

  useEffect(() => {
    if (!hasAutoFocusedRef.current && projectInformation?.projectId && autoSelectRef.current) {
      const autoSelectedInput = autoSelectRef.current.querySelector('input[type="radio"]');
      autoSelectRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      autoSelectedInput?.focus?.({ preventScroll: true });
      hasAutoFocusedRef.current = true;
    }
  }, [projectInformation?.projectId, projects]);

  useEffect(() => {
    let hasTriggered = false;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !loading && !hasTriggered
            && projectsCount >= pageSize * page && projectsCount > 0) {
            hasTriggered = true;
            setPage((prevPage) => prevPage + 1);
            setInitialLoad(false);
          }
        }
      },
      {
        threshold: 1,
        root: scrollableDivRef.current,
        rootMargin: '0px'
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loading, projectsCount, pageSize, page, setPage, setInitialLoad]);

  const handleInlineCreationSuccess = ({ projectId, projectName }) => {
    if (showInlineSuccessAlert) {
      setShowSuccess(true);
      setCreatedProjectName(projectName);
      setCreatedProjectId(projectId);
    }

    triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
      name: 'new project project add',
      location: '',
      items: products.map((prod) => ({
        quantity: prod.quantity || 1,
        price: prod.pricing?.value || prod.price,
        sku: prod.itemId,
        aiQuantityEstimate: prod?.aiQuantityEstimate ? 'yes' : 'no'
      })),
      projectId,
      uniqueSkuCount: new Set(products.map((p) => p.itemId)).size,
      projectAddTotal: products.reduce(
        (total, prod) => total + ((prod.pricing?.value || prod.price || 0) * (prod.quantity || 1)), 0
      ),
    });

    if (onInlineCreationSuccess) {
      onInlineCreationSuccess({ projectId, projectName });
    }
  };

  const projectUrl = createdProjectId ? `/projects/${createdProjectId}` : '#';

  if (showSuccess) {
    return (
      <>
        <DrawerBody>
          <Alert status="success" size="large" onClose={() => setShowSuccess(false)}>
            <Typography>
              <Link
                href={projectUrl}
                weight="bold"
                title={createdProjectName}
              >
                {createdProjectName}
              </Link>
              {' '}successfully created and your Materials have been added to your project
            </Typography>
          </Alert>

          <div className="sui-mt-5">
            <AddToProductDetails products={products} />
          </div>
        </DrawerBody>

        <DrawerFooter
          style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / .375)' }}
        >
          <Button
            variant="primary"
            fullWidth
            onClick={() => handleClose(undefined, { isUserInitiated: true })}
            data-testid="select-project-success-close-button"
          >
            Close
          </Button>
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerBody id="select-project" ref={scrollableDivRef} overflow="scroll">
        {products?.length <= 1 && (
          <AddToProductDetails products={products} />
        )}
        <div className="sui-pl-5">
          {
            projectsExist && (
              <>
                <InlineProjectCreation
                  products={products}
                  singleProduct={singleProduct}
                  multipleProduct={multipleProduct}
                  onSuccess={handleInlineCreationSuccess}
                />
                <div className="sui-pt-3 sui-font-bold">-or-</div>
                <div className="sui-mt-6">
                  <Typography variant="h5" weight="bold">Add to an Existing Project</Typography>
                </div>
                <ProjectSearch onSearch={setSearchTerm} />
                <RadioGroup
                  aria-labelledby="add-to-buttons-project-label"
                  name="radio-buttons-project"
                  value={projectInformation?.projectId}
                >
                  {
                    filteredProjects?.map((project) => (
                      <div
                        key={project.id}
                        ref={project.id === projectInformation?.projectId ? autoSelectRef : null}
                      >
                        <SelectionControlLabel
                          value={project.id}
                          label={project.projectName}
                          onChange={(evt) => { onSelectProject(project); }}
                        >
                          <Radio />
                        </SelectionControlLabel>
                      </div>
                    ))
                  }
                </RadioGroup>
                {loading && (
                  <SkeletonLine fullWidth variant="single" numberOfLines={2} />
                )}
                {!loading && (
                  <div ref={sentinelRef} style={{ height: '1px' }} />
                )}
              </>
            )
          }
        </div>
      </DrawerBody>
      <DrawerFooter position="center" data-testid="add-to-project-add-to-project-footer">
        <div className="sui-flex sui-flex-col sui-gap-1">
          <Button
            data-testid="add-to-project-add-button"
            variant="primary"
            fullWidth
            disabled={!projectsExist || !projectInformation?.projectId}
            onClick={() => { addItemToProject(); }}
            type="submit"
          > Add to Project
          </Button>
          <Button
            data-testid="add-to-project-cancel-button"
            variant="secondary"
            fullWidth
            onClick={() => handleClose(undefined, { isUserInitiated: true })}
            type="submit"
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

SelectProject.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number
  })),
  singleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string
  })),
  multipleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string
  })),
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.string,
    projectName: PropTypes.string
  })),
  projectsExist: PropTypes.bool,
  setSelectedProject: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  projectsCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  setInitialLoad: PropTypes.func.isRequired,
  onInlineCreationSuccess: PropTypes.func,
  showInlineSuccessAlert: PropTypes.bool
};

SelectProject.defaultProps = {
  products: [],
  singleProduct: [],
  multipleProduct: [],
  projects: [],
  projectsExist: false,
  loading: false,
  onInlineCreationSuccess: () => { },
  showInlineSuccessAlert: true
};
